import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Play, Pause, SkipBack, SkipForward, Gauge, VolumeX } from 'lucide-react';

/**
 * Video-style lesson player narrated by the browser's built-in voice (Web Speech API).
 * Free — no API keys. Prefers Indian-English voices. If no voice is available
 * (e.g. some browsers/devices), falls back to silent timed slides.
 */
const RATES = [0.75, 1, 1.25, 1.5];

const pickDefaultVoice = (voices) => {
  const en = voices.filter(v => v.lang && v.lang.toLowerCase().startsWith('en'));
  return (
    en.find(v => v.lang.toLowerCase() === 'en-in') ||
    en.find(v => v.name.toLowerCase().includes('india')) ||
    en.find(v => v.lang.toLowerCase() === 'en-gb') ||
    en[0] || voices[0] || null
  );
};

export const VideoLessonPlayer = ({ slides, onFinished }) => {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1 within current slide
  const [rate, setRate] = useState(1);
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURI] = useState(null);
  const [muteMode, setMuteMode] = useState(false); // true when speech unavailable
  const [finished, setFinished] = useState(false);

  const timerRef = useRef(null);
  const startedRef = useRef(false);
  const stateRef = useRef({ idx: 0, rate: 1, voiceURI: null, playing: false });
  stateRef.current = { idx, rate, voiceURI, playing };

  // Load voices (Chrome loads them async)
  useEffect(() => {
    const load = () => {
      const v = window.speechSynthesis ? window.speechSynthesis.getVoices() : [];
      if (v.length) {
        setVoices(v);
        setVoiceURI((cur) => cur || pickDefaultVoice(v)?.voiceURI || null);
      }
    };
    load();
    if (window.speechSynthesis) window.speechSynthesis.addEventListener('voiceschanged', load);
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.removeEventListener('voiceschanged', load);
        window.speechSynthesis.cancel();
      }
      clearInterval(timerRef.current);
    };
  }, []);

  // Chrome bug workaround: long utterances pause after ~15s unless resumed
  useEffect(() => {
    if (!playing || muteMode) return;
    const keepAlive = setInterval(() => {
      if (window.speechSynthesis && window.speechSynthesis.speaking) window.speechSynthesis.resume();
    }, 10000);
    return () => clearInterval(keepAlive);
  }, [playing, muteMode]);

  const estDuration = useCallback((slide, r) => {
    const words = (slide.narration || '').split(/\s+/).length;
    return Math.max(4, (words / 2.4) / r); // ~2.4 words/sec at 1x
  }, []);

  const stopAll = useCallback(() => {
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    clearInterval(timerRef.current);
  }, []);

  const playSlide = useCallback((i) => {
    if (i >= slides.length) {
      setPlaying(false);
      setFinished(true);
      if (onFinished) onFinished();
      return;
    }
    stopAll();
    setIdx(i);
    setProgress(0);
    setFinished(false);
    setPlaying(true);

    const slide = slides[i];
    const { rate: r, voiceURI: vu } = stateRef.current;
    const duration = estDuration(slide, r);

    // Smooth progress via timer (boundary events are unreliable across voices)
    let elapsed = 0;
    const tick = 100;
    startedRef.current = false;
    const useSpeech = !!window.speechSynthesis && voices.length > 0;

    timerRef.current = setInterval(() => {
      elapsed += tick / 1000;
      if (useSpeech && startedRef.current) {
        setProgress(Math.min(elapsed / duration, 0.97)); // clamp; onend advances
      } else {
        const p = elapsed / duration;
        setProgress(Math.min(p, 1));
        if (p >= 1) playSlide(i + 1); // silent fallback auto-advance
      }
    }, tick);

    if (useSpeech) {
      const u = new SpeechSynthesisUtterance(slide.narration);
      const v = voices.find(x => x.voiceURI === vu);
      if (v) u.voice = v;
      u.rate = r;
      u.onstart = () => { startedRef.current = true; setMuteMode(false); };
      u.onend = () => { clearInterval(timerRef.current); playSlide(i + 1); };
      u.onerror = () => { startedRef.current = false; setMuteMode(true); };
      window.speechSynthesis.speak(u);
      // If speech never starts within 2.5s, switch to silent timed mode
      setTimeout(() => { if (!startedRef.current) setMuteMode(true); }, 2500);
    } else {
      setMuteMode(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides, voices, stopAll, estDuration, onFinished]);

  const togglePlay = () => {
    if (finished) { playSlide(0); return; }
    if (playing) {
      if (window.speechSynthesis) window.speechSynthesis.pause();
      clearInterval(timerRef.current);
      setPlaying(false);
    } else if (window.speechSynthesis && window.speechSynthesis.paused && !muteMode) {
      window.speechSynthesis.resume();
      // restart progress timer from current progress
      const slide = slides[stateRef.current.idx];
      const duration = estDuration(slide, stateRef.current.rate);
      let elapsed = progress * duration;
      timerRef.current = setInterval(() => {
        elapsed += 0.1;
        setProgress(Math.min(elapsed / duration, 0.97));
      }, 100);
      setPlaying(true);
    } else {
      playSlide(idx); // fresh start of current slide
    }
  };

  const changeRate = () => {
    const next = RATES[(RATES.indexOf(rate) + 1) % RATES.length];
    setRate(next);
    stateRef.current.rate = next;
    if (playing) setTimeout(() => playSlide(stateRef.current.idx), 50);
  };

  const changeVoice = (uri) => {
    setVoiceURI(uri);
    stateRef.current.voiceURI = uri;
    if (playing) setTimeout(() => playSlide(stateRef.current.idx), 50);
  };

  const slide = slides[Math.min(idx, slides.length - 1)];
  const overallPct = ((idx + progress) / slides.length) * 100;
  const enVoices = voices.filter(v => v.lang && v.lang.toLowerCase().startsWith('en')).slice(0, 12);

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 shadow-xl" data-testid="video-player">
      {/* Slide canvas */}
      <div className="relative aspect-video sm:aspect-[16/8] p-6 sm:p-10 flex flex-col justify-center select-none">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #38bdf8 0%, transparent 40%), radial-gradient(circle at 80% 70%, #8b5cf6 0%, transparent 40%)' }} />
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-400 mb-2 relative">
          Slide {idx + 1} / {slides.length}
        </p>
        <h2 key={`t${idx}`} className="text-lg sm:text-2xl font-black text-white mb-4 relative animate-in fade-in slide-in-from-bottom-2 duration-500" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="video-slide-title">
          {slide.title}
        </h2>
        <ul className="space-y-2 relative">
          {slide.bullets.map((b, i) => (
            <li key={`${idx}-${i}`} className="flex items-start gap-2.5 text-sm sm:text-base text-slate-200 animate-in fade-in slide-in-from-bottom-1"
              style={{ animationDuration: '400ms', animationDelay: `${i * 350}ms`, animationFillMode: 'both' }}>
              <span className="text-cyan-400 font-black mt-0.5">›</span>{b}
            </li>
          ))}
        </ul>

        {/* Center play overlay */}
        {!playing && (
          <button onClick={togglePlay} data-testid="video-center-play" className="absolute inset-0 flex items-center justify-center bg-slate-950/50 backdrop-blur-[2px] group">
            <span className="w-16 h-16 rounded-full bg-cyan-500 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
              <Play className="w-7 h-7 text-white ml-1" fill="white" />
            </span>
            {finished && <span className="absolute bottom-6 text-xs font-bold text-white/90">Replay lesson ↻</span>}
          </button>
        )}
        {muteMode && playing && (
          <span className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-300 text-[10px] font-bold" data-testid="mute-mode-badge">
            <VolumeX className="w-3 h-3" /> Narration unavailable — auto-playing slides
          </span>
        )}
      </div>

      {/* Progress bar (click to seek by slide) */}
      <div className="flex gap-[3px] px-3 pt-3 bg-slate-900" data-testid="video-progress">
        {slides.map((_, i) => (
          <button key={i} onClick={() => playSlide(i)} data-testid={`video-seek-${i}`}
            className="flex-1 h-1.5 rounded-full bg-slate-700 overflow-hidden hover:h-2.5 transition-all">
            <span className="block h-full bg-cyan-400" style={{ width: i < idx ? '100%' : i === idx ? `${progress * 100}%` : '0%' }} />
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-900">
        <button onClick={() => playSlide(Math.max(0, idx - 1))} className="p-2 rounded-lg text-slate-300 hover:bg-slate-800" data-testid="video-prev-btn"><SkipBack className="w-4 h-4" /></button>
        <button onClick={togglePlay} className="p-2.5 rounded-xl bg-cyan-500 text-white hover:bg-cyan-400" data-testid="video-play-btn">
          {playing ? <Pause className="w-4 h-4" fill="white" /> : <Play className="w-4 h-4 ml-0.5" fill="white" />}
        </button>
        <button onClick={() => playSlide(Math.min(slides.length - 1, idx + 1))} className="p-2 rounded-lg text-slate-300 hover:bg-slate-800" data-testid="video-next-btn"><SkipForward className="w-4 h-4" /></button>
        <span className="text-[11px] font-bold text-slate-400 ml-1 tabular-nums">{Math.round(overallPct)}%</span>
        <div className="flex-1" />
        {enVoices.length > 0 && (
          <select
            value={voiceURI || ''}
            onChange={(e) => changeVoice(e.target.value)}
            data-testid="video-voice-select"
            className="max-w-[130px] sm:max-w-[180px] bg-slate-800 text-slate-200 text-[11px] font-semibold rounded-lg px-2 py-1.5 border border-slate-700 focus:outline-none"
          >
            {enVoices.map(v => <option key={v.voiceURI} value={v.voiceURI}>{v.name.replace(/(Microsoft|Google)\s*/i, '')} ({v.lang})</option>)}
          </select>
        )}
        <button onClick={changeRate} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-slate-800 text-slate-200 text-[11px] font-bold hover:bg-slate-700" data-testid="video-rate-btn">
          <Gauge className="w-3.5 h-3.5" /> {rate}x
        </button>
      </div>
    </div>
  );
};

export default VideoLessonPlayer;
